import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    Field,
    Input,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Text,
    tokens,
} from '@fluentui/react-components';
import { LockClosed20Regular, Person20Regular } from '@fluentui/react-icons';
import { useAuth } from './AuthContext';

const useStyles = makeStyles({
    root: {
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 4fr)',
        backgroundColor: tokens.colorNeutralBackground2,
        '@media (max-width: 899px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    hero: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px',
        color: tokens.colorNeutralForegroundOnBrand,
        backgroundColor: tokens.colorBrandBackground,
        backgroundImage: `radial-gradient(110% 80% at 100% 0%, ${tokens.colorBrandBackgroundHover} 0%, transparent 60%),
            radial-gradient(80% 60% at 0% 100%, ${tokens.colorBrandBackgroundPressed} 0%, transparent 55%)`,
        overflow: 'hidden',
        '@media (max-width: 899px)': {
            display: 'none',
        },
    },
    heroBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
    },
    heroMark: {
        width: '44px',
        height: '44px',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: '#fff',
    },
    heroName: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '-0.01em',
    },
    heroCopy: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '38ch',
    },
    heroTitle: {
        margin: 0,
        fontSize: tokens.fontSizeHero900,
        lineHeight: tokens.lineHeightHero900,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '-0.03em',
        textWrap: 'balance',
    },
    heroText: {
        fontSize: tokens.fontSizeBase400,
        lineHeight: tokens.lineHeightBase400,
        opacity: 0.85,
    },
    heroFoot: {
        fontSize: tokens.fontSizeBase200,
        opacity: 0.7,
    },
    formPane: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
    },
    form: {
        width: '100%',
        maxWidth: '380px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    mobileBrand: {
        display: 'none',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px',
        '@media (max-width: 899px)': {
            display: 'flex',
        },
    },
    mobileMark: {
        width: '40px',
        height: '40px',
        borderRadius: tokens.borderRadiusCircular,
    },
    title: {
        margin: 0,
        fontSize: tokens.fontSizeHero700,
        lineHeight: tokens.lineHeightHero700,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '-0.02em',
    },
    subtitle: {
        color: tokens.colorNeutralForeground3,
    },
    fields: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
});

interface ILocationState {
    from?: { pathname?: string };
}

const LoginPage: React.FC = () => {
    const styles = useStyles();
    const { login, isAuthenticated, initializing } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const redirectTo = (location.state as ILocationState | null)?.from?.pathname ?? '/polls';

    if (!initializing && isAuthenticated) {
        return <Navigate to={redirectTo} replace />;
    }

    const onSubmit = async (event: React.FormEvent): Promise<void> => {
        event.preventDefault();
        if (!username.trim() || !password) {
            setErrorMessage('Укажите логин и пароль.');
            return;
        }
        try {
            setIsSubmitting(true);
            setErrorMessage(undefined);
            await login(username.trim(), password);
            navigate(redirectTo, { replace: true });
        } catch (error) {
            setErrorMessage((error as Error)?.message ?? 'Не удалось войти. Попробуйте снова.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.root}>
            <aside className={styles.hero} aria-hidden>
                <div className={styles.heroBrand}>
                    <img src="/favicon.svg" alt="" className={styles.heroMark} />
                    <Text className={styles.heroName}>Bayer Poll</Text>
                </div>
                <div className={styles.heroCopy}>
                    <h2 className={styles.heroTitle}>Опросы обратной связи для мероприятий</h2>
                    <Text className={styles.heroText}>
                        Создавайте быстрые и комплексные опросы, печатайте QR-коды для аудитории и смотрите результаты в реальном времени.
                    </Text>
                </div>
                <Text className={styles.heroFoot}>Панель администратора</Text>
            </aside>

            <div className={styles.formPane}>
                <form className={styles.form} onSubmit={(event) => { void onSubmit(event); }} noValidate>
                    <div className={styles.mobileBrand}>
                        <img src="/favicon.svg" alt="" className={styles.mobileMark} />
                        <Text weight="semibold" size={400}>Bayer Poll</Text>
                    </div>

                    <div>
                        <h1 className={styles.title}>Вход</h1>
                        <Text className={styles.subtitle} block>Используйте учётную запись администратора опросов.</Text>
                    </div>

                    {errorMessage && (
                        <MessageBar intent="error" role="alert">
                            <MessageBarBody>
                                <MessageBarTitle>Ошибка входа</MessageBarTitle>
                                {errorMessage}
                            </MessageBarBody>
                        </MessageBar>
                    )}

                    <div className={styles.fields}>
                        <Field label="Логин" required>
                            <Input
                                size="large"
                                value={username}
                                autoComplete="username"
                                autoFocus
                                contentBefore={<Person20Regular />}
                                disabled={isSubmitting}
                                onChange={(_, data) => setUsername(data.value)}
                            />
                        </Field>
                        <Field label="Пароль" required>
                            <Input
                                size="large"
                                type="password"
                                value={password}
                                autoComplete="current-password"
                                contentBefore={<LockClosed20Regular />}
                                disabled={isSubmitting}
                                onChange={(_, data) => setPassword(data.value)}
                            />
                        </Field>
                    </div>

                    <Button
                        appearance="primary"
                        type="submit"
                        size="large"
                        disabled={isSubmitting}
                        icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                    >
                        {isSubmitting ? 'Выполняем вход' : 'Войти'}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;
